import authFetch from './fetch';
import axios from 'axios';
import get from 'lodash/get';
import { GOOGLE_API_KEY, GOOGLE_API_LOADER_OPTIONS } from 'shared/../constants/Globals';
import { Loader } from '@googlemaps/js-api-loader';
import { getQueryParams, makeRequest } from './utils';
import { useQuery } from '@tanstack/react-query';
import { CacheConfig } from './consts';

export const getStates = () => makeRequest(authFetch.get('/api/Locations/States'));
export const useGetStates = (opts = {}) => useQuery(['states'], getStates, { ...CacheConfig.forever, ...opts });

export const findCityFromAddressComponents = (address) => {
  const types = ['locality', 'sublocality_level_1', 'sublocality_level_2', 'neighborhood', 'administrative_area_level_3'];

  let city = null;

  for (const type of types) {
    city = address.find((x) => x.types.indexOf(type) > -1);
    if (city) {
      break;
    }
  }

  return city;
};

export const getCityStateFromPostalCode = async (postalCode) => {
  try {
    const data = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?key=${GOOGLE_API_KEY}&language=en&components=country:US|postal_code:${postalCode}`,
    ).then((response) => response.json());
    let ret = { city: null, state: null, county: null };
    if (data.status.toLowerCase() === 'ok' && data.results.length) {
      const { address_components: address } = data.results[0];
      const cityObj = findCityFromAddressComponents(address);
      const stateObj = address.find((x) => x.types.indexOf('administrative_area_level_1') > -1);
      const countyObj = address.find((x) => x.types.indexOf('administrative_area_level_2') > -1);

      ret = {
        city: cityObj && cityObj.long_name ? cityObj.long_name : null,
        state: stateObj && stateObj.short_name ? stateObj.short_name : null,
        county: countyObj ? countyObj.long_name.replace(' County', '') : '',
      };
    }
    return Promise.resolve(ret);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getLocationFromAddress = async (address) => {
  try {
    const { data } = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?key=${GOOGLE_API_KEY}&address=${address}`);

    return Promise.resolve(get(data, 'results[0].geometry.location'));
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const loadGoogleApi = () => {
  const loader = new Loader(GOOGLE_API_LOADER_OPTIONS);
  return loader.load();
};

export const getLocationFromCoordinates = (latitude, longitude, minimal = false) => loadGoogleApi()
  .then((google) => {
    const geocoderService = new google.maps.Geocoder();
    const latLng = new google.maps.LatLng(latitude, longitude);
    return geocoderService.geocode({ latLng });
  })
  .then(({ results }) => {
    const [result] = results;
    if (!result) {
      console.log('error', 'Geocoder: No results found');
      return null;
    }
    const address = minimal
      ? results[0].address_components.find((x) => x.types.indexOf('postal_code') > -1)?.long_name
      : (results[0].formatted_address);
    return address ?? results[0]?.formatted_address;
  })
  .catch((err) => {
    console.log('error', `Geocoder failed due to: ${err}`);
  });

export const getDistanceInMiles = (origin, destination) => loadGoogleApi()
  .then((google) => {
    const distanceMatrixService = new google.maps.DistanceMatrixService();
    const matrixOptions = {
      origins: [`${origin.latitude},${origin.longitude}`],
      destinations: [`${destination.latitude},${destination.longitude}`],
      travelMode: 'DRIVING',
      unitSystem: google.maps.UnitSystem.IMPERIAL,
    };
    return distanceMatrixService.getDistanceMatrix(matrixOptions);
  })
  .then(({ rows }) => {
    const [row] = rows;
    if (!row) {
      console.log('error', 'Distance Matrix Service: No rows found');
      return null;
    }
    const {
      elements: [element],
    } = row;
    if (!element?.distance) {
      return null;
    }
    const {
      distance: { text: milesText },
    } = element;
    const miles = milesText.split(' ')[0].replace(/,/g, ',');
    return miles;
  })
  .catch((err) => {
    console.log('error', `Distance Matrix Service failed due to: ${err}`);
  });

export const getCoordinatesByPostalCode = (postalCode) => loadGoogleApi()
  .then((google) => {
    const geocoderService = new google.maps.Geocoder();
    const options = {
      componentRestrictions: {
        country: 'US',
        postalCode,
      },
    };
    return geocoderService.geocode(options);
  })
  .then(({ results }) => {
    const [result] = results;
    if (!result) {
      console.log('error', 'Geocoder: No results found');
      return null;
    }
    const {
      geometry: { location },
    } = result;
    const coordinates = { latitude: location.lat(), longitude: location.lng() };
    return coordinates;
  })
  .catch((err) => {
    console.log('error', `Geocoder failed due to: ${err}`);
  });

export const hitGetEndpoint = ({ endpoint }) => (params) => authFetch
  .get(`/api${endpoint}${getQueryParams(params)}`)
  .then(({ data }) => data)
  .catch((error) => {
    console.error(error);
    throw error;
  });
